// medplum-settings.js — ONLINE-PRIMARY (main-2) client port of the Settings
// bootstrap (server/opd/view-adapter.mjs buildSettingsView, L2731-2785). Reproduces
// that DTO's exact shape so Settings.jsx (which reads OPDBridge.pick('settings', …))
// is unchanged — the mockups ARE the design (AGENTS.md §7). The orchestrator
// registers loadSettingsFromMedplum under the 'settings' route in bridge.js.
//
// The server built this view ENTIRELY from disk-first stores; under the redesign
// most of it has a cheap FHIR home read run-as-you (the doctor's own token,
// RLS-scoped — see medplum-client.js), and the rest is either static or is
// legacy-only and dropped. Field-by-field:
//
//   doctor{name,department,signatureLine,hospital}
//     • name        = the doctor's OWN Practitioner (medplum.getProfile()), readonly-
//                     granted to every AccessPolicy — always readable, no extra grant.
//     • department  = from the doctor's PractitionerRole (search practitioner=<id>);
//                     falls back to the config's hospital.department / currentUser.
//     • signatureLine — NO natural FHIR field (it lived only in the per-doctor JSON
//                     store). Left '' here; the doctor-preferences fetches (Settings.jsx's
//                     GET/POST /settings) keep their Express path this wave and carry it.
//     • hospital    = the config's hospital.name (the org identity the seed stamps).
//
//   counts{encounters,patients,appointments,documents,media}
//     • five parallel _summary=count searches — the count comes back on Bundle.total
//       with ZERO resources transferred, and each is RLS-scoped for FREE (the doctor's
//       own policy), so these counts are correct-by-construction (the disk-wide Express
//       counts weren't per-user scoped).
//
//   flags — server-process env-var feature flags (FEATURE_OPD_VISION etc.). These are
//     NOT patient/FHIR data — they gate SERVER route behavior and have no FHIR home,
//     so the browser cannot read them. We emit the known FEATURE_OPD_* keys as `false`:
//     production posture is flags-off, and an all-false map is the honest client-side
//     reflection (Settings.jsx renders them read-only). This is a static reflection,
//     NOT a live server read — the real flag state lives only in the server process.
//
//   medplum = null — buildSettingsView's `medplum` block reported the disk↔Medplum
//     MIRROR's health (connection, retryQueueDepth, lastReconcileAt, lastMigration).
//     Under main-2 there is NO disk-first mirror or retry queue, so that diagnostics
//     block is meaningless — null (Settings.jsx already handles a null medplum).
//
//   endpoints + languages — the SAME static literals the server emitted (verbatim).
import { getMedplum, deriveCurrentUser, invalidateCached } from './client-port.js';

// The FEATURE_OPD_* flag universe, mirrored from the server (grep FEATURE_OPD_ over
// server/). Emitted all-false: the browser can't read server-process env state, and
// the production posture is flags-off. A static reflection, not a live read.
const OPD_FLAG_KEYS = [
  'FEATURE_OPD_ENABLED',
  'FEATURE_OPD_CONSULT',
  'FEATURE_OPD_RECEPTION',
  'FEATURE_OPD_PATIENT_INTAKE',
  'FEATURE_OPD_PORTAL',
  'FEATURE_OPD_PAPER_RX',
  'FEATURE_OPD_WHATSAPP',
  'FEATURE_OPD_OFFLINE_AUDIO',
  'FEATURE_OPD_VISION',
  'FEATURE_OPD_VISION_RPPG',
  'FEATURE_OPD_VISION_ANTHRO',
  'FEATURE_OPD_VISION_DERM',
  'FEATURE_OPD_VISION_EYE',
  'FEATURE_OPD_VISION_SYNTHESIS',
];
function flagsAllOff() {
  const flags = {};
  for (const k of OPD_FLAG_KEYS) flags[k] = false;
  return flags;
}

// A _summary=count search: Medplum returns only Bundle.total (no resources), so
// this is the cheapest possible count and it is RLS-scoped by the caller's token.
// Fail-soft to 0 per count so one 403/empty type never blanks the whole card
// (the counts are informational; a real session failure already threw upstream).
async function countOf(medplum, resourceType, params = '') {
  try {
    const sep = params ? '&' : '';
    const bundle = await medplum.search(resourceType, `_summary=count${sep}${params}`);
    return Number(bundle && bundle.total) || 0;
  } catch (_) {
    return 0;
  }
}

// The doctor's department from their own PractitionerRole (best-effort). FHIR
// PractitionerRole.specialty or .code carries the clinical department; we take the
// first coding's display/text. Falls back to the config hint when no role/coding.
async function resolveDepartment(medplum, profile, fallback) {
  try {
    if (!profile || !profile.id) return fallback;
    const roles = await medplum.searchResources('PractitionerRole', `practitioner=Practitioner/${profile.id}&_count=5`);
    for (const role of (roles || [])) {
      const coding = (role.specialty && role.specialty[0] && role.specialty[0].coding && role.specialty[0].coding[0])
        || (role.code && role.code[0] && role.code[0].coding && role.code[0].coding[0]);
      const label = (coding && (coding.display || coding.code))
        || (role.specialty && role.specialty[0] && role.specialty[0].text)
        || (role.code && role.code[0] && role.code[0].text);
      if (label) return label;
    }
  } catch (_) { /* no role / not granted → fall back to the config hint */ }
  return fallback;
}

/** Best human name from a FHIR Practitioner.name (given+family, or text). */
function practitionerName(p) {
  const n = (p && p.name || [])[0];
  if (!n) return null;
  if (n.text) return n.text;
  const given = Array.isArray(n.given) ? n.given.join(' ') : (n.given || '');
  const built = [given, n.family].filter(Boolean).join(' ').trim();
  return built || null;
}

const REG_NO_SYS = 'https://healther.io/opd/registration-no';

/** Qualifications + registration no. from the logged-in Practitioner's qualification[] (admin-staff-invite shape). */
function practitionerIdentityFields(profile) {
  const quals = (profile && profile.qualification) || [];
  const qualTexts = [];
  let registrationNumber = '';
  for (const q of quals) {
    const regId = (q.identifier || []).find((i) => i.system === REG_NO_SYS);
    if (regId) {
      registrationNumber = String(regId.value || '').trim();
    } else {
      const text = q.code && q.code.text;
      if (text) qualTexts.push(String(text).trim());
    }
  }
  return {
    qualifications: qualTexts.join('; '),
    registrationNumber,
  };
}

/**
 * Load the settings bootstrap DTO (the buildSettingsView shape) run-as-you.
 * Registered under the 'settings' route in bridge.js; Settings.jsx is unchanged.
 */
export async function loadSettingsFromMedplum() {
  // ONE session for every caller — /admin/* rides the same 'settings'
  // bootstrap; whether the logged-in user may enter the admin world is
  // App.jsx's role gate over the DERIVED role, not a separate identity.
  const { medplum, hospital } = await getMedplum();

  const profile = medplum.getProfile();
  const currentUser = await deriveCurrentUser(medplum);
  const hospitalName = (hospital && hospital.name) || (currentUser && currentUser.department) || 'Your Hospital';
  // Department hints, in priority order (currentUser.department is a
  // slug like 'cardiology'; hospital.department is the display name).
  const deptHint = (hospital && hospital.department)
    || (currentUser && currentUser.department)
    || 'General Medicine';

  const [department, encounters, patients, appointments, documents, media] = await Promise.all([
    resolveDepartment(medplum, profile, deptHint),
    countOf(medplum, 'Encounter'),
    countOf(medplum, 'Patient'),
    countOf(medplum, 'Appointment'),
    countOf(medplum, 'DocumentReference'),
    countOf(medplum, 'Media'),
  ]);

  const doctorName = practitionerName(profile)
    || (currentUser && (currentUser.displayName || currentUser.name))
    || 'Doctor';
  const { qualifications, registrationNumber } = practitionerIdentityFields(profile);

  return {
    source: 'medplum-client-shaping',
    settings: {
      doctor: {
        name: doctorName,
        department,
        qualifications,
        registrationNumber,
        // No FHIR home for signatureLine — the doctor-prefs Express fetches carry it.
        signatureLine: '',
        hospital: hospitalName,
      },
      flags: flagsAllOff(),
      counts: { encounters, patients, appointments, documents, media },
      // Disk↔Medplum sync diagnostics don't exist under main-2 (no mirror) — null.
      medplum: null,
      // exportJson/resetDemo were dropped 2026-08-05 (W1): those routes died with
      // the disk-sync engine and nothing reads them — health is the one real leg.
      endpoints: {
        health: '/api/v1/health',
      },
      languages: [
        { code: 'en', label: 'English', native: 'English' },
        { code: 'hi', label: 'Hindi', native: 'हिंदी' },
      ],
      generatedAt: new Date().toISOString(),
    },
    currentUser,
    hospital,
  };
}

// ── Per-doctor preferences → Basic{code=doctor-prefs} ────────────────────────
// The doctor's PERSONAL preferences (language, AI tier, notification toggles,
// favourite meds/tests/referrals, signature line) — the payload
// server/opd/settings-store.mjs kept as per-doctor disk JSON — now live in
// Medplum as ONE Basic resource per practitioner, written run-as-you:
//
//   identifier : [{ system: PREFS_ID_SYS, value: <practitionerId> }]  ← the
//                "doctor-prefs|<practitionerId>" conditional-create key
//   code       : { coding:[{ system: PREFS_KIND_SYS, code:'doctor-prefs' }] }
//   subject    : Practitioner/<self>
//   author     : Practitioner/<self>   ← what the RLS grant Basic?author=%profile
//                scopes on, so a doctor can only ever read/write their OWN prefs
//   extension  : [{ url: PREFS_EXT_URL, valueString: <prefs JSON> }]  ← single ext
//
// Same Basic idiom as medplum-attendance.js (healther.io system URLs, searchOne
// + update-or-conditional-create so concurrent first saves converge on one row).
const PREFS_KIND_SYS = 'https://healther.io/opd/basic-kind';   // Basic.code.coding[].system
const PREFS_ID_SYS   = 'https://healther.io/opd/doctor-prefs'; // Basic.identifier[].system
const PREFS_EXT_URL  = 'https://healther.io/opd/doctor-prefs-json';
const PREFS_CODE     = 'doctor-prefs';

const AI_TIERS = ['tier1', 'tier2', 'tier3'];
const THEMES = ['light', 'dark', 'system'];

// Mirrors settings-store.mjs defaults() (minus the identity fields, which stay
// on the settings bootstrap): a brand-new doctor starts from the same seeded
// favourites the disk store gave them, so the consult chips aren't empty.
function defaultPrefs() {
  return {
    signature: '',
    languageDefault: 'hi',
    aiTierPolicy: 'tier2',
    notifications: { whatsapp: true, sms: false, email: false },
    favouriteMeds: [
      { name: 'Paracetamol 500mg', dose: '1 tab', frequency: 'TDS', duration: '5 days' },
      { name: 'Pantoprazole 40mg', dose: '1 tab', frequency: 'OD (before food)', duration: '7 days' },
      { name: 'Cefixime 200mg', dose: '1 tab', frequency: 'BD', duration: '5 days' },
    ],
    favouriteTests: [
      { name: 'CBC' },
      { name: 'Random blood sugar' },
      { name: 'Urine routine' },
      { name: 'X-ray' },
      { name: 'ECG' },
      { name: 'Thyroid profile (TSH)' },
    ],
    favouriteReferrals: [],
  };
}

function isPlainObject(v) {
  return v && typeof v === 'object' && !Array.isArray(v);
}

// Recursive merge, same semantics as settings-store.mjs deepMerge: objects
// merge key-by-key, arrays + scalars replace wholesale (editing favouriteMeds
// replaces the list rather than concatenating).
function deepMergePrefs(base, patch) {
  if (!isPlainObject(patch)) return base;
  const out = Array.isArray(base) ? [...base] : { ...base };
  for (const [k, v] of Object.entries(patch)) {
    if (isPlainObject(v) && isPlainObject(out[k])) out[k] = deepMergePrefs(out[k], v);
    else if (v !== undefined) out[k] = v;
  }
  return out;
}

// Same guard rails settings-store.mjs applied on save, so a bad partial can't
// poison the stored blob (mirrors its normalisation verbatim).
function normalisePrefs(prefs) {
  const p = { ...prefs };
  if (!AI_TIERS.includes(p.aiTierPolicy)) p.aiTierPolicy = 'tier2';
  if (p.languageDefault !== 'en' && p.languageDefault !== 'hi') p.languageDefault = 'hi';
  if (!Array.isArray(p.favouriteMeds)) p.favouriteMeds = [];
  p.favouriteMeds = p.favouriteMeds
    .filter((m) => isPlainObject(m) && String(m.name || '').trim())
    .map((m) => ({
      name: String(m.name || '').trim(),
      dose: String(m.dose || '').trim(),
      frequency: String(m.frequency || '').trim(),
      duration: String(m.duration || '').trim(),
    }));
  if (!Array.isArray(p.favouriteTests)) p.favouriteTests = [];
  p.favouriteTests = p.favouriteTests
    .filter((t) => isPlainObject(t) && String(t.name || '').trim())
    .map((t) => {
      const region = String(t.region || '').trim();
      return region ? { name: String(t.name).trim(), region } : { name: String(t.name).trim() };
    });
  if (!Array.isArray(p.favouriteReferrals)) p.favouriteReferrals = [];
  p.favouriteReferrals = p.favouriteReferrals
    .filter((r) => isPlainObject(r) && String(r.departmentId || '').trim())
    .map((r) => ({
      departmentId: String(r.departmentId || '').trim(),
      departmentName: String(r.departmentName || '').trim(),
      doctorId: String(r.doctorId || '').trim(),
      doctorName: String(r.doctorName || '').trim(),
    }));
  if (p.theme !== undefined && !THEMES.includes(p.theme)) delete p.theme;
  if (!isPlainObject(p.notifications)) p.notifications = { whatsapp: true, sms: false, email: false };
  p.notifications = {
    whatsapp: !!p.notifications.whatsapp,
    sms: !!p.notifications.sms,
    email: !!p.notifications.email,
  };
  if (p.signature !== undefined) p.signature = String(p.signature || '');
  return p;
}

function prefsSearch(practitionerId) {
  return `identifier=${PREFS_ID_SYS}|${encodeURIComponent(practitionerId)}`;
}

// Extract the prefs JSON from the Basic's single extension. A missing or
// corrupt blob degrades to {} (defaults win) — same posture as the disk
// store's readStored(); the resource itself is only ever written by savePrefs.
function parsePrefs(resource) {
  const ext = ((resource && resource.extension) || []).find((e) => e && e.url === PREFS_EXT_URL);
  if (!ext || typeof ext.valueString !== 'string') return {};
  try { return JSON.parse(ext.valueString) || {}; } catch (_) { return {}; }
}

async function prefsSession() {
  const { medplum } = await getMedplum();
  const profile = medplum.getProfile();
  if (!profile || !profile.id) throw new Error('doctor-prefs: no Medplum profile on this session');
  return { medplum, profile };
}

/**
 * Load the logged-in practitioner's preferences (deep-merged over defaults).
 * Always returns a complete, well-shaped prefs object — the settings-store
 * getSettings() contract, run-as-you against Medplum. (The legacy
 * `{ identity }` option some callers still pass is ignored: there is ONE
 * session per browser now.)
 */
export async function loadPrefs() {
  const { medplum, profile } = await prefsSession();
  const existing = await medplum.searchOne('Basic', prefsSearch(profile.id));
  return normalisePrefs(deepMergePrefs(defaultPrefs(), existing ? parsePrefs(existing) : {}));
}

/**
 * The logged-in doctor's SAVED favourite meds/tests, read straight from their
 * doctor-prefs Basic (RLS-scoped: Basic?author=%profile). Returns the STORED
 * values ONLY — no default backfill — so the consult bootstrap (medplum-encounter.js)
 * can ship the doctor's own favourites while the consult screen keeps its built-in
 * DEFAULT_FAVS as the fallback when the doctor has saved none. Both fields are null
 * when there is no prefs Basic yet (a brand-new doctor) or the stored list is empty,
 * so the screen's `P('settings.favouriteMeds', null)` gate falls through to defaults.
 * Fail-soft to nulls: a favourites read must never block the consult graph loading.
 */
export async function loadFavouritesForConsult() {
  try {
    const { medplum } = await getMedplum();
    const profile = medplum.getProfile();
    if (!profile || !profile.id) return { favouriteMeds: null, favouriteTests: null };
    const existing = await medplum.searchOne('Basic', prefsSearch(profile.id));
    if (!existing) return { favouriteMeds: null, favouriteTests: null };
    const stored = parsePrefs(existing);
    const meds = Array.isArray(stored.favouriteMeds) && stored.favouriteMeds.length ? stored.favouriteMeds : null;
    const tests = Array.isArray(stored.favouriteTests) && stored.favouriteTests.length ? stored.favouriteTests : null;
    return { favouriteMeds: meds, favouriteTests: tests };
  } catch (_) {
    return { favouriteMeds: null, favouriteTests: null };
  }
}

/**
 * Deep-merge `partial` into the practitioner's stored prefs and persist —
 * update-in-place when the Basic exists, conditional-create on the
 * doctor-prefs|<practitionerId> identifier when it doesn't (idempotent under
 * a concurrent first save). Returns the full merged prefs object.
 */
export async function savePrefs(partial = {}) {
  const { medplum, profile } = await prefsSession();
  const selfRef = { reference: `${profile.resourceType}/${profile.id}` };
  const existing = await medplum.searchOne('Basic', prefsSearch(profile.id));
  const current = deepMergePrefs(defaultPrefs(), existing ? parsePrefs(existing) : {});
  const merged = normalisePrefs(deepMergePrefs(current, partial || {}));
  const body = {
    resourceType: 'Basic',
    identifier: [{ system: PREFS_ID_SYS, value: profile.id }],
    code: { coding: [{ system: PREFS_KIND_SYS, code: PREFS_CODE }], text: PREFS_CODE },
    subject: selfRef,
    author: selfRef, // the RLS row-ownership key (Basic?author=%profile)
    extension: [{ url: PREFS_EXT_URL, valueString: JSON.stringify(merged) }],
  };
  if (existing) await medplum.updateResource({ ...existing, ...body, id: existing.id });
  else await medplum.createResourceIfNoneExist(body, prefsSearch(profile.id));
  // Settings.jsx uses this return value directly, but the consult bootstrap
  // re-reads the SAME prefs Basic (loadFavouritesForConsult) — without the bust a
  // consult opened right after editing favourites loads the pre-edit list.
  await invalidateCached('Basic');
  return merged;
}
